import React, { useState, useEffect } from 'react';

const MODE_LABELS = {
    simple:     { label: 'Simple Agent',     color: '#16a34a', bg: '#f0fdf4' },
    structured: { label: 'Structured Agent', color: '#d97706', bg: '#fffbeb' },
    flow:       { label: 'Flow Mode',        color: '#1565c0', bg: '#eff6ff' },
};

// ── Template Preview Panel ────────────────────────────────────────────────────
function PreviewPanel({ template, onUse, onClose }) {
    if (!template) return null;
    const mode = MODE_LABELS[template.mode] || MODE_LABELS.simple;

    return (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 420, height: '100vh', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <span style={{ fontSize: 32 }}>{template.icon}</span>
                <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{template.name}</p>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: mode.bg, color: mode.color }}>{mode.label}</span>
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 18, padding: 4 }}>
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                {/* Description */}
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6 }}>{template.description}</p>

                {/* Sample conversation */}
                {template.sample_conversation?.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>Sample Conversation</p>
                        <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {template.sample_conversation.map((msg, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                    <div style={{ maxWidth: '80%', padding: '8px 12px', borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: msg.role === 'user' ? '#dcf8c6' : '#fff', border: '1px solid #e5e7eb', fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                                        {msg.text}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Objective */}
                {template.objective && (
                    <div style={{ marginBottom: 20 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Objective</p>
                        <p style={{ fontSize: 12, color: '#6b7280', margin: 0, lineHeight: 1.6, background: '#f8fafc', padding: '10px 14px', borderRadius: 8 }}>{template.objective}</p>
                    </div>
                )}

                {/* Variables */}
                {template.variables?.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Variables ({template.variables.length})</p>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {template.variables.map((v, i) => (
                                <span key={i} style={{ padding: '3px 10px', background: '#f3f4f6', borderRadius: 20, fontSize: 11, fontFamily: 'monospace', color: '#374151' }}>{'{{'}{v}{'}}'}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* FAQs */}
                {template.faqs?.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Inline FAQs ({template.faqs.length})</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {template.faqs.slice(0, 3).map((faq, i) => (
                                <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', margin: '0 0 2px' }}>Q: {faq.question}</p>
                                    <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>A: {faq.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Guardrails */}
                {template.guardrails?.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Guardrails ({template.guardrails.length})</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {template.guardrails.map((g, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#374151' }}>
                                    <i className="bi bi-shield-check" style={{ color: '#1565c0', fontSize: 11, marginTop: 1, flexShrink: 0 }}></i>
                                    {g}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 10, flexShrink: 0 }}>
                <button onClick={onClose}
                    style={{ flex: 1, padding: '10px', background: '#fff', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    Close
                </button>
                <button onClick={() => onUse(template)}
                    style={{ flex: 2, padding: '10px', background: 'linear-gradient(135deg, #1565c0, #1e40af)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <i className="bi bi-rocket"></i> Use This Template
                </button>
            </div>
        </div>
    );
}

// ── Template Card ─────────────────────────────────────────────────────────────
function TemplateCard({ template, onPreview, onUse }) {
    const mode = MODE_LABELS[template.mode] || MODE_LABELS.simple;
    return (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: 10 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#1565c0'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(21,101,192,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 28, flexShrink: 0 }}>{template.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>{template.name}</p>
                        {template.is_popular && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#fef3c7', color: '#d97706' }}>🔥 Popular</span>}
                        {template.is_new && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#eff6ff', color: '#1565c0' }}>🆕 New</span>}
                    </div>
                    <p style={{ fontSize: 11, color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{template.description}</p>
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: mode.bg, color: mode.color }}>{mode.label}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={e => { e.stopPropagation(); onPreview(template); }}
                        style={{ padding: '5px 10px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                        Preview
                    </button>
                    <button onClick={e => { e.stopPropagation(); onUse(template); }}
                        style={{ padding: '5px 10px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                        Use
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Main Step0 Component ──────────────────────────────────────────────────────
export default function Step0({ form, set, setStep, applyTemplate, url, token }) {
    const [view,         setView]         = useState('choice'); // 'choice' | 'scratch' | 'template'
    const [templates,    setTemplates]    = useState([]);
    const [industries,   setIndustries]   = useState([]);
    const [industry,     setIndustry]     = useState('all');
    const [search,       setSearch]       = useState('');
    const [loading,      setLoading]      = useState(false);
    const [preview,      setPreview]      = useState(null);

    // Load industries on mount
    useEffect(() => {
        (async () => {
            try {
                const res  = await fetch(`${url}/agent-templates/meta/industries`, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (data.success) setIndustries(data.data);
            } catch (e) { /* use fallback */ }
        })();
    }, []);

    // Load templates when industry or search changes
    useEffect(() => {
        if (view !== 'template') return;
        (async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (industry !== 'all') params.set('industry', industry);
                if (search) params.set('search', search);
                const res  = await fetch(`${url}/agent-templates?${params}`, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (data.success) setTemplates(data.data);
            } catch (e) { setTemplates([]); }
            setLoading(false);
        })();
    }, [view, industry, search]);

    const handleUseTemplate = (tpl) => {
        setPreview(null);
        applyTemplate(tpl);
    };

    // ── View: Choice ───────────────────────────────────────────────────────────
    if (view === 'choice') return (
        <div>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px', textAlign: 'center' }}>How would you like to start?</p>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 32px', textAlign: 'center' }}>Choose a path to create your WhatsApp AI agent</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 680, margin: '0 auto' }}>
                {/* Start Fresh */}
                <div onClick={() => setView('scratch')}
                    style={{ padding: 32, border: '2px solid #e5e7eb', borderRadius: 20, cursor: 'pointer', background: '#fff', textAlign: 'center', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#1565c0'; e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(21,101,192,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
                    <p style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>Start Fresh</p>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6 }}>Build your agent from scratch with full control over every setting</p>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#1565c0' }}>
                        Choose your mode <i className="bi bi-arrow-right"></i>
                    </span>
                </div>

                {/* Use Template */}
                <div onClick={() => setView('template')}
                    style={{ padding: 32, border: '2px solid #e5e7eb', borderRadius: 20, cursor: 'pointer', background: '#fff', textAlign: 'center', transition: 'all 0.15s', position: 'relative', overflow: 'hidden' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.background = '#faf5ff'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(124,58,237,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ position: 'absolute', top: 12, right: 12, fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: '#7c3aed', color: '#fff' }}>
                        50+ Templates
                    </div>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                    <p style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>Use Template</p>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6 }}>Pick from 50+ ready-made agents for your industry. Go live in minutes.</p>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#7c3aed' }}>
                        Browse templates <i className="bi bi-arrow-right"></i>
                    </span>
                </div>
            </div>
        </div>
    );

    // ── View: Scratch ──────────────────────────────────────────────────────────
    if (view === 'scratch') return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                <button onClick={() => setView('choice')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="bi bi-arrow-left"></i> Back
                </button>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>Choose Agent Mode</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                {[
                    { mode: 'simple',     icon: 'bi-lightning-charge', title: 'Simple Agent',
                      desc: 'One prompt. AI handles the entire conversation intelligently.',
                      best: 'Best for: Support, FAQ, Lead gen, Reminders',
                      time: '2 min setup', color: '#16a34a', bg: '#f0fdf4' },
                    { mode: 'structured', icon: 'bi-layers', title: 'Structured Agent',
                      desc: 'Define conversation stages. AI drives within each stage.',
                      best: 'Best for: Sales, Onboarding, Complex qualification',
                      time: '10 min setup', color: '#d97706', bg: '#fffbeb' },
                    { mode: 'flow',       icon: 'bi-diagram-3', title: 'Flow Mode',
                      desc: 'Full control with visual flow builder. Every step defined by you.',
                      best: 'Best for: Compliance, Regulated processes, Data collection',
                      time: 'Custom setup', color: '#1565c0', bg: '#eff6ff' },
                ].map(m => (
                    <div key={m.mode} onClick={() => { set('mode', m.mode); setStep(1); }}
                        style={{ padding: 24, border: `2px solid ${form.mode === m.mode ? m.color : '#e5e7eb'}`, borderRadius: 16, cursor: 'pointer', background: form.mode === m.mode ? m.bg : '#fff', transition: 'all 0.15s' }}
                        onMouseEnter={e => { if (form.mode !== m.mode) { e.currentTarget.style.borderColor = m.color; e.currentTarget.style.background = m.bg; }}}
                        onMouseLeave={e => { if (form.mode !== m.mode) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}}>
                        <i className={`bi ${m.icon}`} style={{ fontSize: 28, color: m.color, display: 'block', marginBottom: 14 }}></i>
                        <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>{m.title}</p>
                        <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>{m.desc}</p>
                        <p style={{ fontSize: 11, color: m.color, fontWeight: 600, margin: '0 0 8px' }}>{m.best}</p>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: m.bg, color: m.color, border: `1px solid ${m.color}20` }}>
                            ⏱ {m.time}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    // ── View: Template ─────────────────────────────────────────────────────────
    return (
        <div style={{ display: 'flex', gap: 0, height: 520, border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden' }}>

            {/* Left sidebar — industries */}
            <div style={{ width: 200, background: '#f8fafc', borderRight: '1px solid #e5e7eb', overflowY: 'auto', flexShrink: 0 }}>
                <div style={{ padding: '12px 12px 8px' }}>
                    <button onClick={() => setView('choice')} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 12, padding: '4px 0', marginBottom: 8 }}>
                        <i className="bi bi-arrow-left" style={{ fontSize: 11 }}></i> Back
                    </button>
                    {/* Search */}
                    <div style={{ position: 'relative', marginBottom: 8 }}>
                        <i className="bi bi-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 11 }}></i>
                        <input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search templates..."
                            style={{ width: '100%', padding: '6px 8px 6px 26px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, outline: 'none', background: '#fff', boxSizing: 'border-box' }}/>
                    </div>
                </div>
                {/* Industry list */}
                <div style={{ padding: '0 8px 12px' }}>
                    {industries.map(ind => (
                        <button key={ind.id} onClick={() => setIndustry(ind.id)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '8px 10px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: industry === ind.id ? 700 : 500, background: industry === ind.id ? '#eff6ff' : 'transparent', color: industry === ind.id ? '#1565c0' : '#374151', textAlign: 'left', marginBottom: 2 }}
                            onMouseEnter={e => { if (industry !== ind.id) e.currentTarget.style.background = '#f0f4f8'; }}
                            onMouseLeave={e => { if (industry !== ind.id) e.currentTarget.style.background = 'transparent'; }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{ind.icon}</span>
                                <span style={{ fontSize: 11 }}>{ind.label}</span>
                            </span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: industry === ind.id ? '#1565c0' : '#9ca3af' }}>{ind.count}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Right — template cards */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <div className="spinner-border text-primary" style={{ width: '1.5rem', height: '1.5rem' }}></div>
                    </div>
                ) : templates.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
                        <p style={{ fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>No templates found</p>
                        <p style={{ fontSize: 12, margin: 0 }}>Try a different search or industry</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {templates.map(tpl => (
                            <TemplateCard key={tpl._id || tpl.id} template={tpl}
                                onPreview={setPreview}
                                onUse={handleUseTemplate}/>
                        ))}
                    </div>
                )}
            </div>

            {/* Preview panel */}
            {preview && (
                <>
                    <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 199 }}/>
                    <PreviewPanel template={preview} onUse={handleUseTemplate} onClose={() => setPreview(null)}/>
                </>
            )}
        </div>
    );
}
